import { useEffect, useMemo, useState } from "react";
import { Box, Button, FormControl, InputAdornment, OutlinedInput, TablePagination, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Table from "../../../components/Table";
import ActionsMenu from "../../../components/ActionsMenu";
import api from "../../../utils/api";
import { PromocodeColumns } from "./components/PromocodeColumns";
import PromocodeModal from "./components/PromocodeModal";

export default function Promocodes() {
    const columns = useMemo(() => PromocodeColumns(), []);
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [activeFilters, setActiveFilters] = useState({});
    const [row, setRow] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchRows = async (filters = activeFilters, page = pagination.page) => {
        setLoading(true);
        try {
            const response = await api.get("admin/promocodes", {
                params: {
                    filter: filters,
                    page,
                    perPage: pagination.perPage,
                },
            });
            setRows(response.data?.data || []);
            const p = response.data?.meta?.pagination || {};
            setPagination((prev) => ({
                ...prev,
                page: p.page || page,
                total: p.total || 0,
                totalPages: p.totalPages || 0,
            }));
        } catch (e) {
            console.error("Failed to load promocodes", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            const filters = search.trim() ? { search: search.trim() } : {};
            setActiveFilters(filters);
            fetchRows(filters, 1);
        }, 350);
        return () => clearTimeout(timer);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDelete = async (id, code) => {
        try {
            await api.delete(`admin/promocodes/${id}`);
            fetchRows(activeFilters, pagination.page);
        } catch (e) {
            window.alert(e.response?.data?.message || `Failed to delete promocode "${code}".`);
        }
    };

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Promocodes
            </Typography>
            <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between", gap: 2 }}>
                <FormControl sx={{ width: { xs: "100%", md: "45ch" } }} variant="outlined">
                    <OutlinedInput
                        size="small"
                        placeholder="Search by code..."
                        sx={{ background: "#fff" }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        startAdornment={(
                            <InputAdornment position="start">
                                <SearchRoundedIcon fontSize="small" />
                            </InputAdornment>
                        )}
                    />
                </FormControl>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { setRow(null); setIsModalOpen(true); }}>
                    Create New
                </Button>
            </Box>

            <Table
                loading={loading}
                rows={rows}
                columns={columns}
                selactable={false}
                draggable={false}
                renderActions={(r) => (
                    <ActionsMenu
                        actions={[
                            { label: "Edit", onClick: () => { setRow(r); setIsModalOpen(true); } },
                            {
                                label: "Delete",
                                onClick: () => handleDelete(r.id, r.code),
                                confirm: true,
                                confirmText: `Are you sure you want to delete "${r.code}"?`,
                            },
                        ]}
                    />
                )}
            />

            <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                rowsPerPage={pagination.perPage}
                rowsPerPageOptions={[]}
                onPageChange={(event, newPage) => fetchRows(activeFilters, newPage + 1)}
            />

            <PromocodeModal
                open={isModalOpen}
                row={row}
                onClose={(refetch) => {
                    setIsModalOpen(false);
                    setRow(null);
                    if (refetch) fetchRows(activeFilters, pagination.page);
                }}
            />
        </Box>
    );
}
